import { z } from 'zod';

/**
 * Plans the canonical user record can be on. Free is the default for
 * any newly-registered account; the rest are upgraded via Stripe.
 */
export const UserPlanSchema = z.enum(['free', 'starter', 'team', 'enterprise']);
export type UserPlan = z.infer<typeof UserPlanSchema>;

/**
 * Public user record returned by /me, login, register. Note: never
 * exposes password hash or org-internal fields — that lives only in the
 * DB. Extra fields the API may return:
 *   - isNewUser (only on register/login flows)
 *   - hasCompletedOnboarding
 *   - onboardingFirstFlowCompleted (Migration 082 — Session 5 of the
 *     P1 head-to-head roadmap; persists the editor guided-tour
 *     completion server-side so the tour fires exactly once per
 *     user, not per-device)
 */
export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  avatar: z.string(),
  plan: UserPlanSchema,
  createdAt: z.string(),
  isNewUser: z.boolean().optional(),
  hasCompletedOnboarding: z.boolean().optional(),
  onboardingFirstFlowCompleted: z.boolean().optional(),
});
export type User = z.infer<typeof UserSchema>;

// ---- Login ----
export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const LoginResponseSchema = z.object({
  user: UserSchema,
  token: z.string(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ---- Register ----
export const RegisterRequestSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  password: z.string().min(8),
  acceptedTerms: z.boolean().refine((v) => v === true, 'Terms must be accepted.'),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const RegisterResponseSchema = LoginResponseSchema;
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

// ---- Forgot / reset password ----
export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

// ---- /me ----
export const MeResponseSchema = UserSchema;
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ---- MFA ----
export const MfaStatusSchema = z.object({
  enabled: z.boolean(),
  pendingSetup: z.boolean(),
  enabledAt: z.string().nullable(),
  recoveryCodesRemaining: z.number().int().nonnegative(),
  lastUsedAt: z.string().nullable(),
});
export type MfaStatus = z.infer<typeof MfaStatusSchema>;

export const MfaEnrollResponseSchema = z.object({
  qrSvg: z.string(),
  secret: z.string(),
});
export type MfaEnrollResponse = z.infer<typeof MfaEnrollResponseSchema>;

export const MfaVerifyRequestSchema = z.object({
  code: z.string().min(6).max(8),
});
export type MfaVerifyRequest = z.infer<typeof MfaVerifyRequestSchema>;

// ---- API keys (public surface) ----
export const ApiKeyMetaSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type ApiKeyMeta = z.infer<typeof ApiKeyMetaSchema>;

export const CreateApiKeyRequestSchema = z.object({
  name: z.string().min(1).max(120),
  scopes: z.array(z.string()).optional(),
  expiresAt: z.string().nullable().optional(),
});
export type CreateApiKeyRequest = z.infer<typeof CreateApiKeyRequestSchema>;

export const CreateApiKeyResponseSchema = z.object({
  key: ApiKeyMetaSchema,
  /** Plaintext key; only returned at creation time. */
  secret: z.string(),
});
export type CreateApiKeyResponse = z.infer<typeof CreateApiKeyResponseSchema>;
